import { useEffect, useMemo, useSyncExternalStore } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  ConnectionMode,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import FullscreenExitOutlined from "@mui/icons-material/FullscreenExitOutlined";
import { mergeEquipmentTypes } from "../equipmentTypes";
import { nodeTypes } from "../EquipmentNode";
import { edgeTypes } from "../EquipmentEdge";
import { buildEdgesComStyle } from "../templates/templateUtils";
import { getAccessibleNodeColor } from "../nodeColorPalette";
import {
  ensureCustomEquipmentTypesLoaded,
  getCustomEquipmentTypesSnapshot,
  subscribeCustomEquipmentTypes,
} from "../customEquipmentTypes/customEquipmentTypesStore";
import { useHostStatus } from "../hostStatusStore";
import styles, { getDiagramDotColor } from "../styles";

// Corpo do preview, dentro do provider para acesso à instância do React Flow
const DiagramaEquipamentosPreviewInterno = ({
  nodes: nodesProp = [],
  edges: edgesProp = [],
  isExpanded = false,
  onToggleExpanded = () => {},
  isActive = true,
  hostOptions = [],
}) => {
  const theme = useTheme();
  const canvasStyles = styles(theme);
  // Mesma logo (e mesma lógica de escolha por modo) da barra de ações do canvas de edição
  // ampliado — ver diagramaEquipamentos/index.js
  const logoExternoDark =
    window?.config?.REACT_APP_LOGO_EXTERNO_DARK ||
    process.env.REACT_APP_LOGO_EXTERNO_DARK ||
    "";
  const logoExternoLight =
    window?.config?.REACT_APP_LOGO_EXTERNO_LIGHT ||
    process.env.REACT_APP_LOGO_EXTERNO_LIGHT ||
    "";
  const logo =
    theme?.palette?.mode === "light"
      ? logoExternoLight || logoExternoDark
      : logoExternoDark || logoExternoLight;
  const { fitView } = useReactFlow();

  // Catálogo global de tipos personalizados: mesmo store do canvas de edição,
  // garante que os ícones apareçam iguais fora do modo de edição também
  const customEquipmentTypes = useSyncExternalStore(
    subscribeCustomEquipmentTypes,
    getCustomEquipmentTypesSnapshot,
  );

  useEffect(() => {
    ensureCustomEquipmentTypesLoaded();
  }, []);

  const equipmentTypesMesclados = useMemo(
    () => mergeEquipmentTypes(customEquipmentTypes),
    [customEquipmentTypes],
  );

  // hostIds distintos vinculados a algum nó — status buscado no mesmo store compartilhado do
  // canvas de edição (hostStatusStore), em lote com o de todas as demais abas ativas
  const linkedHostIds = useMemo(
    () => [...new Set((nodesProp || []).map((node) => node?.data?.hostId).filter(Boolean))],
    [nodesProp],
  );
  const hostStatusMap = useHostStatus(linkedHostIds, isActive);

  // Nome de exibição do host por hostId — mesma resolução do canvas de edição (nodesForCanvas em
  // diagramaEquipamentos/index.js), usado só no tooltip do pontinho de status, nunca persistido
  const hostLabelByHostId = useMemo(
    () => Object.fromEntries((hostOptions || []).map((opcao) => [opcao?.hostId, opcao?.host])),
    [hostOptions],
  );

  // Injeta o catálogo mesclado, a cor de agrupamento e o status do host vinculado só para
  // renderização, igual ao canvas principal (nunca persistido)
  const nodes = useMemo(
    () =>
      (nodesProp || []).map((node) => ({
        ...node,
        // Com nodesDraggable/nodesConnectable/elementsSelectable todos false, o React Flow marca
        // o wrapper do node com pointer-events:none inline (só liga sozinho se algum desses ficar
        // true ou se onNodeClick/onNodeMouseEnter for passado pro <ReactFlow>) — sem isso nenhum
        // hover chega ao node, e os Tooltips (observações, status do host) nunca abrem no preview.
        // Reativa só o pointer-events; clique/arraste continuam bloqueados pelas props acima.
        style: { ...node?.style, pointerEvents: "all" },
        data: {
          ...node?.data,
          _equipmentTypes: equipmentTypesMesclados,
          _colorSwatch: getAccessibleNodeColor(node?.data?.cor, theme),
          _hostStatus: node?.data?.hostId ? hostStatusMap[node.data.hostId] : undefined,
          _hostLabel: node?.data?.hostId ? hostLabelByHostId[node.data.hostId] : undefined,
        },
      })),
    [nodesProp, equipmentTypesMesclados, theme, hostStatusMap, hostLabelByHostId],
  );

  // Estilo das edges re-derivado do lineStyle com o tema atual, igual ao preview de template
  const edges = useMemo(
    () => buildEdgesComStyle(edgesProp || [], theme),
    [edgesProp, theme],
  );

  // Controla fechamento por teclado e impede rolagem da página atrás do overlay
  useEffect(() => {
    if (!isExpanded) return undefined;

    const previousOverflow = document?.body?.style?.overflow ?? "";
    const handleKeyDown = (evento) => {
      if (evento?.key === "Escape") onToggleExpanded?.();
    };

    document?.addEventListener?.("keydown", handleKeyDown);
    if (document?.body?.style) document.body.style.overflow = "hidden";

    return () => {
      document?.removeEventListener?.("keydown", handleKeyDown);
      if (document?.body?.style)
        document.body.style.overflow = previousOverflow;
    };
  }, [isExpanded, onToggleExpanded]);

  // Reenquadra conteúdo depois que overlay recebe dimensões da viewport
  useEffect(() => {
    if (!isExpanded) return undefined;

    const frame = window?.requestAnimationFrame?.(() =>
      fitView({ padding: 0.2 }),
    );

    return () => window?.cancelAnimationFrame?.(frame);
  }, [fitView, isExpanded]);

  return (
    <Box
      sx={{
        ...canvasStyles.canvasShell,
        ...(isExpanded ? canvasStyles.canvasShellExpanded : {}),
      }}
      role={isExpanded ? "dialog" : undefined}
      aria-modal={isExpanded ? true : undefined}
      aria-label={isExpanded ? "Diagrama de equipamentos ampliado" : undefined}
    >
      {isExpanded && (
        <Box sx={canvasStyles.canvasActionBar}>
          <img src={logo} alt="Logo" style={canvasStyles.canvasActionLogo} />
          <Tooltip title="Reduzir diagrama" arrow>
            <IconButton
              size="small"
              sx={canvasStyles.canvasActionButton}
              onClick={onToggleExpanded}
              aria-label="Reduzir diagrama"
            >
              <FullscreenExitOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Box
        sx={{
          ...canvasStyles.canvasWrapper,
          ...(isExpanded ? canvasStyles.canvasWrapperExpanded : {}),
        }}
      >
        <ReactFlow
          proOptions={{ hideAttribution: true }}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          connectionMode={ConnectionMode.Loose}
          colorMode={theme?.palette?.mode}
          defaultEdgeOptions={{
            type: "equipamento",
            labelStyle: {
              fill: theme?.palette?.text?.primary,
              fontSize: 11,
              fontWeight: 600,
            },
            labelBgStyle: {
              fill: theme?.palette?.background?.paper,
              fillOpacity: 0.9,
            },
            labelBgPadding: [6, 3],
            labelBgBorderRadius: 4,
          }}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color={getDiagramDotColor(theme)}
          />
        </ReactFlow>
      </Box>
    </Box>
  );
};

// Preview read-only do diagrama de equipamentos de um circuito, reaproveitando
// nó/aresta do diagrama real (mesmo padrão do preview de template salvo)
export default function DiagramaEquipamentosPreview({
  nodes = [],
  edges = [],
  isExpanded = false,
  onToggleExpanded = () => {},
  // Falso quando esta é uma aba de circuito oculta (view mode também mantém todas montadas, ver
  // CLAUDE.md): sem esse gate, cada aba oculta continuaria pedindo status de host em segundo
  // plano no store compartilhado (hostStatusStore) mesmo sem estar sendo visualizada
  isActive = true,
  // Mesmas opções de host do combobox de edição (hostOptions) — usadas só para resolver o nome de
  // exibição no tooltip do pontinho de status; sem isso o tooltip caía no hostId cru
  hostOptions = [],
}) {
  return (
    <ReactFlowProvider>
      <DiagramaEquipamentosPreviewInterno
        nodes={nodes}
        edges={edges}
        isExpanded={isExpanded}
        onToggleExpanded={onToggleExpanded}
        isActive={isActive}
        hostOptions={hostOptions}
      />
    </ReactFlowProvider>
  );
}
